import { TENANT_KEY } from '../utility/env.js';

/**
 * PROFOLIO V2 THEME
 * =================
 * Every value of the v2 look in one place: teal becomes the v2 green, the slate greys move onto the v2
 * greys, and English text moves to Geist.
 *
 * Source of truth: "Profolio 2.0 Look: Font & Colour Mapping" (mapping-filled.md) on
 * https://app.clickup.com/t/z8v0kmv11c. Values that file does not list come from the ticket's colour map
 * (colour-map.csv) and say so.
 *
 * ./index.js builds the app theme from this file for the markets in section 1, so every page, antd
 * component and Less style takes the v2 look from here. Colours and fonts written directly in components
 * go through v2Color() and v2Font(), and teal illustration files through v2Asset() (section 7).
 *
 *   1. The switch
 *   2. The v2 palette
 *   3. Fonts
 *   4. Today's colour → its v2 colour
 *   5. Teal inside icon art
 *   6. The app theme's keys in v2
 *   7. v2Color(), v2Font() and v2Asset()
 */

/* 1. THE SWITCH ------------------------------------------------------------------------------------- */

// Markets on the v2 look. Zameen is not one of them and keeps today's look.
const V2_MARKETS = ['bayut', 'oman', 'bahrain', 'qatar', 'jordan', 'eg'];

export const IS_V2_THEME = V2_MARKETS.includes(TENANT_KEY);

/* 2. THE V2 PALETTE (the design token name is in each comment) --------------------------------------- */

export const V2_TOKENS = Object.freeze({
  // Brand green
  brandGreen: '#28B16D', // brand-green: fills, borders, icons
  brandGreenPressed: '#208E57', // brand-green-pressed: brand text on light backgrounds, hover, pressed
  brandGreenDeep: '#10603A', // brand-green-deep (colour map): the darkest brand art
  statusGood: '#249F62', // status-good: green text (#28B16D is only 2.8:1 on white)

  // Green tints, darkest first
  primary7: '#53C18A', // primary-7 (colour map)
  primary6: '#69C899', // primary-6 (colour map)
  primary5: '#7ED0A7', // primary-5 (colour map)
  primary4: '#93D8B6', // primary-4 (colour map)
  primary3: '#A9E0C5', // primary-3 (colour map)
  primary2: '#BEE8D3', // primary-2
  tintGreen200: '#D4EFE2', // tint-green-200
  tintGreen100: '#E9F7F0', // tint-green-100
  tintGreen050: '#F0FAF5', // tint-green-050

  // Text and icons
  textBlack: '#000000', // text-black
  textPrimary: '#222222', // text-primary: main text, headings
  textSecondary: '#4F4F4F', // text-secondary
  textTertiary: '#626262', // text-tertiary
  textMuted: '#707070', // text-muted
  textFaint: '#9D9D9D', // text-faint: faint text, placeholders
  glyphCool: '#8A9099', // glyph-cool: light grey icons
  textOnBrand: '#FFFFFF', // text-on-brand

  // Borders
  borderStrong: '#DEDEDE', // border-strong
  borderBase: '#E6E6E6', // border-base: dividers
  borderSoft: '#F0F0F0', // border-soft: default border

  // Surfaces
  surfaceDim: '#D6D6D6', // surface-dim: disabled and empty fills
  surfaceStrong: '#EDEDED', // surface-strong
  surfacePageWeb: '#F3F4F5', // surface-page-web: grey backgrounds, tags
  surfaceMuted: '#F5F5F5', // surface-muted: steps, pills
  surfacePage: '#F6F7FB', // surface-page: page background
  surfaceSubtle: '#FAFAFA', // surface-subtle: footer background
  surfaceFaint: '#FCFCFC', // surface-faint: the lightest fills
  surfaceCard: '#FFFFFF', // surface-card
});

const T = V2_TOKENS;

/* 3. FONTS ------------------------------------------------------------------------------------------- */

export const V2_FONTS = Object.freeze({
  // English text in Geist. Geist has no Arabic glyphs, so Arabic falls through to Droid Arabic Kufi (no
  // change) and the Latin characters inside Arabic UI render in Geist.
  family: "Geist, 'Droid Arabic Kufi', sans-serif",
  // Geist as one variable font, weights 100–900, loaded on every page (member area included) in place of Lato.
  // Figtree stays in the request only for the headings the new My Listings design sets in it.
  stylesheet:
    'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&family=Figtree:ital,wght@0,300..900;1,300..900&display=swap',
});

// Font weights do not change: every weight keeps its number (300, 400, 500, 600, 700, 800). Geist ships a
// real file for each, so the weights Lato only faked (300, 500, 600, 800) now render as designed.

/* 4. TODAY'S COLOUR → ITS V2 COLOUR ------------------------------------------------------------------- */

// A single value serves every use. { fill, text }: fills, borders and icons take `fill`, text takes `text`.
//
// Not listed, so they keep today's value:
//   - the greys v2 already uses: #222222 #4F4F4F #626262 #707070 #9D9D9D #E6E6E6 #F6F7FB #F5F5F5 #FAFAFA
//   - blue, red, amber, purple and pink; #98DAB9 (listing health "good"); WhatsApp, Google, dubizzle and
//     Zameen colours; black and white washes; shadows

const BRAND_TEAL = {
  '#006169': { fill: T.brandGreen, text: T.brandGreenPressed }, // main brand colour, primary buttons
  '#005960': T.brandGreenPressed, // darker teal: hover, pressed
  '#A6C8CA': T.primary2, // light tint 1
  '#CCDFE1': T.tintGreen200, // light tint 2
  '#E1F2F0': T.tintGreen100, // light tint 3
  '#F2FAFA': T.tintGreen050, // light tint 4
  '#F7FCFC': T.tintGreen050, // lightest tint
};

const GREEN = {
  '#28B16D': { fill: T.brandGreen, text: T.statusGood }, // green buttons, success (the fill is already v2)
  '#0CAB7C': { fill: T.brandGreenPressed, text: T.statusGood }, // green button hover
};

const TEXT_GREYS = {
  '#272B41': T.textPrimary, // dark text
  '#131623': T.textPrimary, // dark hover
  '#363A51': T.textSecondary, // grey hover
  '#5A5F7D': { fill: T.textMuted, text: T.textTertiary }, // secondary text
  '#767676': T.textMuted, // post-listing chip text
  '#868EAE': { fill: T.glyphCool, text: T.textFaint }, // light grey text, icons
  '#ADB4D2': T.textFaint, // extra-light text
  '#BABABA': T.textFaint, // light grey
};

const DISABLED_GREYS = {
  '#C2C2C2': { fill: T.surfaceDim, text: T.textFaint }, // disabled, empty
  '#BDBDBD': { fill: T.surfaceDim, text: T.textFaint }, // disabled, empty
  '#C1BFBF': { fill: T.surfaceDim, text: T.textFaint }, // listing health "default"
};

const BORDER_GREYS = {
  '#F0F0F0': { fill: T.borderSoft, text: T.textOnBrand }, // default border (no change); white as text
  '#F1F2F6': T.borderSoft, // light border
  '#DEDEDE': { fill: T.borderStrong, text: T.textFaint }, // normal border (no change); faint as text
  '#C6D0DC': T.borderStrong, // strong border
};

const BACKGROUND_GREYS = {
  '#F8F9FB': T.surfacePage, // light grey background
  '#F4F5F7': T.surfacePageWeb, // grey background
  '#EFF0F3': T.surfacePageWeb, // darker grey background, tags
  '#E2E6EA': T.borderBase, // light hover
};

/* 5. TEAL INSIDE ICON ART ------------------------------------------------------------------------------ */

// Every other teal the code paints, mostly inside SVG icons and illustrations, grouped by the v2 green it
// becomes. Not in the md: these come from the colour map. Greys inside icon art keep today's value.
// prettier-ignore
const ART_TEAL_GROUPS = [
  [T.brandGreenPressed, ['#005158', '#005257', '#009688', '#025459', '#033F46', '#053940', '#098F79', '#0A5158',
    '#0A6158', '#0C5A61', '#0D9488', '#0E8073', '#13C2C2', '#16847D', '#1A8F7A', '#2F413D']],
  [T.statusGood, ['#00A488', '#0A9A8F']],
  [T.brandGreen, ['#00B1D2', '#18A8C9', '#38B49D']],
  [T.primary7, ['#02858E']],
  [T.primary6, ['#00CCCC', '#1292A6', '#388B91', '#55969B', '#58C1CA', '#5E9499']],
  [T.primary5, ['#00D2FF', '#04D4E6', '#19D3C5', '#5ECCC8', '#66A0A5', '#71A8AC', '#73CCC3', '#79CDD1']],
  [T.primary4, ['#00DFFF', '#30E8BF', '#53D8E3', '#59B5C3', '#7DD5F4']],
  [T.primary3, ['#00E3FF', '#80DFFF', '#8EB9BC']],
  [T.primary2, ['#8BEAE7', '#ACC9CB', '#B3D0D2', '#B3D4D6', '#BCEAF9']],
  [T.tintGreen200, ['#C1D9DA', '#C9F7F6', '#CBE0E3', '#CEEDF0', '#CFD6D7', '#CFE7E8', '#D5EDE8', '#D8E7E8']],
  [T.tintGreen100, ['#C7FFFF', '#DDEAEB', '#DDF7F0', '#E0EEF2', '#E2EDEE', '#E5EFF0', '#E6F4EF', '#E6F5F5',
    '#E7F3EF', '#E8F5F6', '#E8F6F9', '#E8FAF4', '#EAF9F3']],
  [T.tintGreen050, ['#E7ECED', '#E9EEF0', '#E9F1F1', '#EDF4F5', '#EEF6F5', '#F0FAF6', '#F2F8FA', '#F3FAF9',
    '#F4FBFA', '#F5FAF8', '#F5FAFA', '#F5FBFB']],
];

const ART_TEAL = {
  ...Object.fromEntries(ART_TEAL_GROUPS.flatMap(([to, colors]) => colors.map((from) => [from, to]))),
  // Teals that are also used as text, where they take brand-green-pressed
  '#00745F': { fill: T.brandGreenDeep, text: T.brandGreenPressed }, // TruBroker badge
  '#2B7B82': { fill: T.primary7, text: T.brandGreenPressed },
  '#71A7AC': { fill: T.primary5, text: T.brandGreenPressed },
};

// Every teal: the only colours that also change inside icon art and illustrations.
export const V2_TEAL = { ...BRAND_TEAL, ...ART_TEAL };

export const V2_COLOR_MAP = {
  ...V2_TEAL,
  ...GREEN,
  ...TEXT_GREYS,
  ...DISABLED_GREYS,
  ...BORDER_GREYS,
  ...BACKGROUND_GREYS,
};

/* 6. THE APP THEME'S KEYS IN V2 ----------------------------------------------------------------------- */

// The palette of ./index.js (`themeColors`) with its v2 values. ./index.js builds every theme key and antd
// token from this palette, so these are all it takes to recolour them. A key used both as text and as a fill
// takes the value of its main use; the other use reads V2_COLOR_MAP.
//
// No change: gray900, gray800, gray700, gray600, gray400, gray300, gray200, gray100, baseColor,
// headingColor, grayDarkColor, lightColor, borderColorNormal, white, and every non-grey colour.
export const V2_THEME_COLORS = {
  primaryColor: T.brandGreen,
  primaryHover: T.brandGreenPressed, // the v2 button hover
  primaryTextColor: T.brandGreenPressed, // brand colour used as text
  primaryLight: T.tintGreen050,
  primaryLight1: T.primary2,
  primaryLight2: T.tintGreen200,
  primaryLight3: T.tintGreen100,
  primaryLight4: T.tintGreen050,

  successHover: T.brandGreenPressed,
  successTextColor: T.statusGood, // green used as text

  darkColor: T.textPrimary,
  darkHover: T.textPrimary,
  grayHover: T.textSecondary,
  grayColor: T.textTertiary, // as a fill or icon: text-muted
  whiteHover: T.textTertiary,
  lightGrayColor: T.textFaint, // as an icon: glyph-cool
  extraLightColor: T.textFaint,
  gray550: T.textFaint,
  gray500: T.surfaceDim, // icons and borders; as text: text-faint
  grayLightestColor: T.textFaint, // as a fill: surface-dim
  healthColorDefault: T.surfaceDim, // as text: text-faint
  lightHover: T.borderBase,

  borderColorLight: T.borderSoft,
  borderColorDeep: T.borderStrong,
  bgGrayColorLight: T.surfacePage,
  bgGrayColorNormal: T.surfacePageWeb,
  bgGrayColorDeep: T.surfacePageWeb,
};

// Theme keys ./index.js writes as literals instead of reading the palette.
export const V2_THEME_KEYS = {
  'font-family': V2_FONTS.family,
  'tag-default-bg': T.surfacePageWeb,
  'post-listing-chip-text-color': T.textMuted,
};

// antd tokens ./index.js does not derive from the palette. Without them antd works out the primary's hover,
// text and tint shades itself, and they come out lighter than the v2 ones.
export const V2_ANTD_TOKENS = {
  fontFamily: V2_FONTS.family,
  colorPrimaryHover: T.brandGreenPressed,
  colorPrimaryActive: T.brandGreenPressed,
  colorPrimaryText: T.brandGreenPressed,
  colorPrimaryTextHover: T.brandGreenPressed,
  colorPrimaryTextActive: T.brandGreenPressed,
  colorPrimaryBg: T.tintGreen050,
  colorPrimaryBgHover: T.tintGreen100,
  colorPrimaryBorder: T.tintGreen200,
  colorPrimaryBorderHover: T.primary2,
  colorSuccessText: T.statusGood,
};

export const V2_ANTD_COMPONENTS = {
  Tabs: {
    inkBarColor: T.brandGreen,
    itemSelectedColor: T.brandGreenPressed,
    itemHoverColor: T.brandGreenPressed,
    itemActiveColor: T.brandGreenPressed,
  },
};

/* 7. v2Color(), v2Font() AND v2Asset() ----------------------------------------------------------------- */

const lookUp = (hex, use) => {
  const mapped = V2_COLOR_MAP[hex.toUpperCase()];
  return mapped && (typeof mapped === 'string' ? mapped : mapped[use]);
};
const toHex = (r, g, b) => `#${[r, g, b].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
const toRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(', ');

// For colours written directly in components rather than read from the theme. Takes today's colour, as #rrggbb,
// #rrggbbaa, rgb() or rgba(), and gives its v2 colour; `use` is 'fill' (fills, borders, icons) or 'text'.
// Transparency is kept (#006169aa → #28B16Daa, rgba(0, 97, 105, 0.3) → rgba(40, 177, 109, 0.3)). Off the v2
// look, and for anything the map does not list, the colour comes back as it was given.
export const v2Color = (color, use = 'fill') => {
  if (!IS_V2_THEME || typeof color !== 'string') return color;
  const hex = color.trim().match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (hex) {
    const to = lookUp(`#${hex[1]}`, use);
    return to ? `${to}${hex[2] || ''}` : color;
  }
  const rgb = color.trim().match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+%?)\s*)?\)$/i);
  const to = rgb && lookUp(toHex(rgb[1], rgb[2], rgb[3]), use);
  if (!to) return color;
  return rgb[4] === undefined ? `rgb(${toRgb(to)})` : `rgba(${toRgb(to)}, ${rgb[4]})`;
};

// For font stacks written directly in components: Geist on the v2 look, today's stack everywhere else.
export const v2Font = (fontFamily) => (IS_V2_THEME ? V2_FONTS.family : fontFamily);

// Illustration files painted in teal have a v2 copy beside them ("-v2" before ".svg"): the teal swapped for its
// v2 colour, every other colour as it was. The two loaded with require() (tru-broker-bg.svg, no-data.svg) pick
// their copy with IS_V2_THEME where they are required.
const V2_ASSETS = new Set([
  '/profolio-assets/images/lead-managment-popup.svg',
  '/profolio-assets/images/limit-credits.svg',
  '/profolio-assets/images/limit-post-listing.svg',
  '/profolio-assets/images/maintenance.svg',
  '/profolio-assets/images/user-profile.svg',
  '/profolio-assets/eg/lite/upsell.svg',
]);

// For an illustration's URL path: its v2 copy on the v2 look, the file as it is everywhere else.
export const v2Asset = (path) => (IS_V2_THEME && V2_ASSETS.has(path) ? path.replace(/\.svg$/, '-v2.svg') : path);
